import { brand, defaultSessionConfig } from "@studystraight/shared-config";
import type { PostureBaseline, StudySession } from "@studystraight/shared-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { formatMinutes, formatTime } from "./app/format.js";
import { defaultSessionSetup, type Screen, type SessionSetup } from "./app/navigation.js";
import type { AppSettings } from "./app/settings.js";
import { CalibrationScreen } from "./features/calibration/CalibrationScreen.js";
import { HistoryScreen } from "./features/history/HistoryScreen.js";
import { HomeScreen } from "./features/home/HomeScreen.js";
import { MonitoringScreen } from "./features/monitoring/MonitoringScreen.js";
import { ReportScreen } from "./features/report/ReportScreen.js";
import { SettingsScreen } from "./features/settings/SettingsScreen.js";
import { SetupScreen } from "./features/setup/SetupScreen.js";
import { useSessionRepository } from "./hooks/useSessionRepository.js";
import { primeAlertClips } from "./platform/alert/alertClips.js";
import { unlockAudio } from "./platform/alert/audioUnlock.js";
import { createSettingsStore } from "./platform/storage/settingsStore.js";
import {
  acceptRecovery,
  discardRecovery,
  findRecoverableSession,
  type RecoverableSession,
} from "./platform/storage/recovery.js";

/**
 * Router tự viết thay vì `react-router` — §36 (không over-engineering).
 *
 * App chỉ có 7 screen, không cần deep-link (§33 không yêu cầu), và màn hình monitoring **không
 * được** unmount khi người dùng bấm back (mất stream + baseline). Một `useState` kiểm soát rõ
 * ràng hơn là chống lại history stack của browser.
 */
export function App(): React.JSX.Element {
  const [screen, setScreen] = useState<Screen>("home");
  const [setup, setSetup] = useState<SessionSetup>(defaultSessionSetup);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [baseline, setBaseline] = useState<PostureBaseline | null>(null);
  const [lastSession, setLastSession] = useState<StudySession | null>(null);
  const [recovery, setRecovery] = useState<RecoverableSession | null>(null);

  const store = useMemo(() => createSettingsStore(), []);
  const { repository, status, failed } = useSessionRepository();

  useEffect(() => {
    let disposed = false;
    void store.load().then((loaded) => {
      if (disposed) return;
      setSettings(loaded);
    });
    return () => {
      disposed = true;
    };
  }, [store]);

  /**
   * Ghi settings mỗi lần đổi.
   *
   * `updateSettings` chứ không phải một effect theo `[settings]`: effect sẽ ghi lại ngay chính
   * giá trị vừa load xong ở lần mount đầu, và ghi cả những lần state đổi vì lý do khác.
   */
  const updateSettings = useCallback(
    (next: AppSettings) => {
      setSettings(next);
      void store.save(next);
    },
    [store],
  );

  /**
   * Đổi lựa chọn của phiên → ghi luôn xuống store.
   *
   * `settings` đọc qua ref: `changeSetup` được truyền xuống SetupScreen, nếu nó đổi identity theo
   * `settings` thì mọi lần lưu lại tạo một callback mới. Ref cũng là cách duy nhất dùng được
   * `settings` ở đây, vì early-return "đang tải cài đặt" nằm sau các hook.
   */
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const changeSetup = useCallback(
    (next: SessionSetup) => {
      setSetup(next);
      const current = settingsRef.current;
      // Chỉ ghi khi placement THẬT SỰ đổi: `onSetupChange` cũng được gọi từ banner
      // placement-mismatch, và một lần write IndexedDB không đổi gì là một lần có thể lỗi.
      if (current !== null && current.placement !== next.placement) {
        updateSettings({ ...current, placement: next.placement });
      }
    },
    [updateSettings],
  );

  // Tìm phiên dở dang một lần, ngay khi có repository (§27). Chạy trước cả khi người dùng bấm gì:
  // nếu đợi họ vào History thì snapshot có thể đã bị ghi đè bởi phiên mới.
  const checked = useRef(false);
  useEffect(() => {
    if (repository === null || checked.current) return;
    checked.current = true;
    void findRecoverableSession(repository, defaultSessionConfig.minReportableDurationMs).then(
      setRecovery,
    );
  }, [repository]);

  const goHome = useCallback(() => setScreen("home"), []);

  /**
   * Setup → Calibration, kèm mở khoá audio.
   *
   * Dùng chung cho **mọi** đường rời màn Setup chứ không viết inline: bản trước chỉ mở khoá ở
   * `case "setup"`, còn SetupScreen dự phòng ở `case "monitoring"` (baseline mất) thì không — đi qua
   * nhánh đó là chạy trọn phiên học không có tiếng nào, và không có gì trên UI cho thấy điều đó.
   */
  const beginCalibration = useCallback(() => {
    // Mở khoá NGAY trong handler của cú bấm này: iOS chỉ cho phép resume AudioContext bên trong một
    // user gesture, và đây là cú bấm cuối cùng trước khi phiên học chạy (§20).
    unlockAudio();
    // Hai lời gọi vì iOS mở khoá theo từng đường riêng: `AudioContext` (chime) và từng `<audio>`
    // element (câu nhắc thu sẵn) không dùng chung quyền phát.
    primeAlertClips();
    setScreen("calibration");
  }, []);

  /**
   * Mỗi phiên mới bắt đầu bằng placement đã lưu.
   *
   * Seed ở đây chứ không ở effect load settings: người dùng có thể đổi placement trong Settings
   * giữa hai phiên, và nếu chỉ seed một lần lúc mở app thì Camera Setup vẫn hiện lựa chọn cũ —
   * đúng cái "phải config lại" mà settings đang cố tránh.
   */
  const startSetup = useCallback((from: AppSettings) => {
    setSetup({ placement: from.placement });
    setScreen("setup");
  }, []);

  const finishSession = useCallback((session: StudySession) => {
    setLastSession(session);
    setBaseline(null);
    setScreen("report");
  }, []);

  if (settings === null) {
    // Không mount screen nào bằng settings mặc định rồi đổi sau: một phiên học có thể bắt đầu với
    // mục tiêu 25 phút rồi bị nhảy sang 45 phút giữa đường.
    return (
      <main className="screen screen--centered">
        <h1 className="brand">{brand.name}</h1>
        <p className="muted">Đang tải cài đặt…</p>
      </main>
    );
  }

  switch (screen) {
    case "home":
      return (
        <HomeScreen
          onStart={() => startSetup(settings)}
          onHistory={() => setScreen("history")}
          onSettings={() => setScreen("settings")}
          storageUnavailable={status !== null && !status.persistent}
          recovery={
            recovery === null || repository === null
              ? null
              : {
                  label: `${formatTime(recovery.session.startTime)} · ${formatMinutes(
                    recovery.session.duration,
                  )}`,
                  onAccept: () => {
                    void acceptRecovery(repository, recovery).then((session) => {
                      setRecovery(null);
                      setLastSession(session);
                      setScreen("report");
                    });
                  },
                  onDiscard: () => {
                    void discardRecovery(repository).then(() => setRecovery(null));
                  },
                }
          }
        />
      );

    case "setup":
      return (
        <SetupScreen
          setup={setup}
          onSetupChange={changeSetup}
          settings={settings}
          onSettingsChange={updateSettings}
          onBack={goHome}
          onReady={beginCalibration}
        />
      );

    case "calibration":
      return (
        <CalibrationScreen
          placement={setup.placement}
          settings={settings}
          onBack={() => setScreen("setup")}
          onDone={(next) => {
            setBaseline(next);
            setScreen("monitoring");
          }}
        />
      );

    case "monitoring":
      // Vào monitoring mà không có baseline/repository là không thể theo luồng bình thường, nhưng
      // nếu xảy ra thì quay về Setup an toàn hơn là chạy phiên với baseline giả.
      if (baseline === null || repository === null) {
        return (
          <SetupScreen
            setup={setup}
            onSetupChange={changeSetup}
            settings={settings}
            onSettingsChange={updateSettings}
            onBack={goHome}
            onReady={beginCalibration}
          />
        );
      }
      return (
        <MonitoringScreen
          baseline={baseline}
          settings={settings}
          repository={repository}
          storageFailed={failed}
          onSettingsChange={updateSettings}
          onFinish={finishSession}
          onRecalibrate={() => {
            setBaseline(null);
            setScreen("calibration");
          }}
        />
      );

    case "report":
      if (lastSession === null) {
        return (
          <main className="screen screen--centered">
            <p className="muted">Chưa có phiên học nào để xem.</p>
            <button type="button" className="btn btn--secondary" onClick={goHome}>
              Về trang chính
            </button>
          </main>
        );
      }
      return (
        <ReportScreen
          session={lastSession}
          onHome={goHome}
          onHistory={() => setScreen("history")}
        />
      );

    case "history":
      return (
        <HistoryScreen
          repository={repository}
          persistent={status?.persistent ?? true}
          onBack={goHome}
          onOpen={(session) => {
            setLastSession(session);
            setScreen("report");
          }}
        />
      );

    case "settings":
      return (
        <SettingsScreen
          settings={settings}
          onChange={updateSettings}
          repository={repository}
          persistent={status?.persistent ?? true}
          onBack={goHome}
        />
      );
  }
}
